import { useEffect, useState } from 'react';
import { Stack, Tooltip, UnstyledButton, Avatar, Divider, Image, Text } from '@mantine/core';
import { IconHome2, IconLogout, IconUsers, IconBuildingStore, IconBinoculars, IconPencil, IconSettings, IconBan } from '@tabler/icons-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '../auth/useAuth';
import useApi from '../hooks/useApi';
import { fetchMyPrincipal } from '../services/api';
import pkg from '../../package.json';

function getInitials(name) {
  if (!name) return 'U';
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || 'U';
}

function NavIcon({ label, to, Icon, active, onClick }) {
  return (
    <Tooltip label={label} position="right" withArrow>
      <UnstyledButton
        component={Link}
        to={to}
        onClick={onClick}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          width: 52,
          height: 52,
          borderRadius: 8,
          backgroundColor: active ? 'var(--mantine-color-sage-7)' : 'transparent',
          color: active ? '#fff' : 'var(--mantine-color-sage-3)',
          transition: 'background-color 120ms ease',
        }}
      >
        <Icon size={20} />
        <Text size="10px" fw={500} style={{ lineHeight: 1 }}>{label}</Text>
      </UnstyledButton>
    </Tooltip>
  );
}

// Board-split Phase 1: one nav item per enabled flow. Coach flow → People
// board, business flow → Businesses board. Defaults to People while loading
// or when the principal has no flows set.
const BOARD_NAV_ITEMS = {
  coach: { label: 'People', to: '/v2/board/people', Icon: IconUsers },
  business: { label: 'Businesses', to: '/v2/board/businesses', Icon: IconBuildingStore },
};

export default function V2Navbar({ onNavClick }) {
  const { user, logout } = useAuth0();
  const api = useApi();
  const location = useLocation();
  const path = location.pathname;
  const [enabledFlows, setEnabledFlows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchMyPrincipal(api)
      .then((principal) => {
        if (cancelled) return;
        const flows = Array.isArray(principal?.enabledFlows) ? principal.enabledFlows : [];
        setEnabledFlows(flows.filter((f) => f === 'coach' || f === 'business'));
      })
      .catch(() => {
        if (!cancelled) setEnabledFlows([]);
      });
    return () => { cancelled = true; };
  }, [api]);

  const boardFlows = (enabledFlows && enabledFlows.length) ? enabledFlows : ['coach'];
  // Board items light up for the board and every per-person detail/approval
  // view (they're the drill-down from the board). When both flows are shown,
  // the Businesses item only claims its own board path.
  const isBusinessBoard = path.startsWith('/v2/board/businesses');
  const isPipelineish = path.startsWith('/v2/board')
    || path.startsWith('/v2/leads')
    || path.startsWith('/v2/pipeline');
  const boardItemActive = (flow) => {
    if (!isPipelineish) return false;
    if (boardFlows.length === 1) return true;
    return flow === 'business' ? isBusinessBoard : !isBusinessBoard;
  };
  // Prospecting lights up for the whole campaign flow (list + detail).
  const isProspecting = path.startsWith('/v2/campaigns');
  const isArticles = path.startsWith('/v2/articles') || path.startsWith('/v2/knowledge-base');
  const isOptOuts = path.startsWith('/v2/opt-outs');
  const isSettings = path.startsWith('/v2/settings');
  // "today" is /v2 root (excluding sub-routes).
  const isToday = path === '/v2' && !location.search.includes('new=1');

  return (
    <Stack
      h="100%"
      w={80}
      bg="aviary.8"
      align="center"
      justify="space-between"
      py="md"
      gap={0}
    >
      <Stack align="center" gap="lg">
        <Image src="/logo_small.png" w={72} h={72} fit="contain" style={{ flexShrink: 0 }} />

        <Divider color="aviary.7" w={40} />

        <Stack gap={6} align="center">
          <NavIcon label="Today" to="/v2" Icon={IconHome2} active={isToday} onClick={onNavClick} />
          {boardFlows.map((flow) => {
            const item = BOARD_NAV_ITEMS[flow] || BOARD_NAV_ITEMS.coach;
            return (
              <NavIcon
                key={flow}
                label={item.label}
                to={item.to}
                Icon={item.Icon}
                active={boardItemActive(flow)}
                onClick={onNavClick}
              />
            );
          })}
          <NavIcon label="Prospecting" to="/v2/campaigns" Icon={IconBinoculars} active={isProspecting} onClick={onNavClick} />
          <NavIcon label="Articles" to="/v2/articles" Icon={IconPencil} active={isArticles} onClick={onNavClick} />
          <NavIcon label="Opt-outs" to="/v2/opt-outs" Icon={IconBan} active={isOptOuts} onClick={onNavClick} />
        </Stack>
      </Stack>

      <Stack align="center" gap="sm">
        <Divider color="aviary.7" w={40} />

        <NavIcon label="Settings" to="/v2/settings" Icon={IconSettings} active={isSettings} onClick={onNavClick} />

        <Tooltip
          label={`${user?.name || 'User'}\n${user?.email || ''}`}
          position="right"
          withArrow
          multiline
          maw={200}
        >
          <Avatar radius="xl" size="sm" color="sage" style={{ cursor: 'default' }}>
            {getInitials(user?.name)}
          </Avatar>
        </Tooltip>

        <Tooltip label="Logout" position="right" withArrow>
          <UnstyledButton
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 44,
              height: 44,
              borderRadius: 8,
              color: 'var(--mantine-color-sage-4)',
              transition: 'background-color 120ms ease',
            }}
          >
            <IconLogout size={20} />
          </UnstyledButton>
        </Tooltip>

        <Text size="xs" c="aviary.6" style={{ fontVariantNumeric: 'tabular-nums' }}>
          v{pkg.version}
        </Text>
      </Stack>
    </Stack>
  );
}
